import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  InputAdornment,
  Chip,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { formatCurrency } from "../../utils/formatters";

const CategoryRatesSection = ({
  trainer,
  availableCategories,
  onOpenAddDialog,
  onUpdateCategoryRate,
  onDeleteCategoryRate,
}) => {
  const [editingRateId, setEditingRateId] = useState(null);
  const [editingRateValue, setEditingRateValue] = useState("");

  const handleStartEdit = (rate) => {
    setEditingRateId(rate.id);
    setEditingRateValue(rate.hourly_rate?.toString() || "");
  };

  const handleCancelEdit = () => {
    setEditingRateId(null);
    setEditingRateValue("");
  };

  const handleSaveEdit = async (rateId) => {
    const success = await onUpdateCategoryRate(trainer.id, rateId, editingRateValue);
    if (success) {
      setEditingRateId(null);
      setEditingRateValue("");
    }
  };

  return (
    <Box
      sx={{
        py: 2,
        px: { xs: 1, sm: 2 },
        bgcolor: "grey.50",
        borderRadius: 1,
        my: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CategoryIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Stawki dla kategorii zajęć
          </Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => onOpenAddDialog(trainer.id)}
          disabled={availableCategories.length === 0}
        >
          Dodaj
        </Button>
      </Box>

      {trainer.category_rates && trainer.category_rates.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kategoria</TableCell>
                <TableCell align="right">Stawka godzinowa</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>
                  Akcje
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trainer.category_rates.map((rate) => (
                <TableRow key={rate.id}>
                  <TableCell>
                    <Chip
                      label={
                        rate.lesson_category_name ||
                        `Kategoria ${rate.lesson_category_id}`
                      }
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {editingRateId === rate.id ? (
                      <TextField
                        value={editingRateValue}
                        onChange={(e) => setEditingRateValue(e.target.value)}
                        type="number"
                        size="small"
                        sx={{ width: 100 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">PLN</InputAdornment>
                          ),
                        }}
                        autoFocus
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "primary.main" }}
                      >
                        {formatCurrency(rate.hourly_rate)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingRateId === rate.id ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleSaveEdit(rate.id)}
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={handleCancelEdit}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title="Edytuj">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(rate)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Usuń">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              onDeleteCategoryRate(trainer.id, rate.id)
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 2 }}
        >
          Brak stawek dla kategorii. Zostanie użyta stawka domyślna.
        </Typography>
      )}
    </Box>
  );
};

export default CategoryRatesSection;
